import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback } from 'react';

import QuantityButton from '@/components/common/QuantityButtonV2/QuantityButtonV2';
import ProductLikePill from '@/components/menu/ProductLikePill/ProductLikePill';
import type { ProductModelType } from '@/models/api';
import type { OrderPlan } from '@/models/cart/order-plan';
import { useStore } from '@/models/root-store';
import { Mode } from '@/utils/constants';
import datocmsImageLoader from '@/utils/datocms-image-loader';
import { trackProductClicked } from '@/utils/track/tracker.helper';

import Button from '../Button/Button';
import { SecondaryTag } from '../SecondaryTag/SecondaryTag';
import { TertiaryTag } from '../TertiaryTag/TertiaryTag';

export type LineItemTypes = {
  plan: OrderPlan;
  product: ProductModelType;
};

const RecommendationProductTile: React.FC<LineItemTypes> = observer(
  ({ plan, product }: LineItemTypes) => {
    const { grossPrice, promotionTotal, cms, slug } = product;
    const name = cms?.primaryName ?? product.name;
    const image = cms ? cms.tileImage?.url : '';
    const secondaryDisplayTag = cms ? cms.secondaryDisplayTag : null;
    const tertiaryDisplayTag = cms ? cms.tertiaryDisplayTag : null;

    const {
      cartStore: { cart, cartToUse },
    } = useStore();

    const { mode } = cartToUse;
    const { closeCart } = cart;
    const qty = plan?.productQtyBySku[product.sku]?.qty || 0;

    const discountable =
      (product.isDiscountable && plan?.discount) ||
      product.hasSubProducts ||
      promotionTotal;
    const discountStyle = discountable
      ? 'text-xs line-through font-interMedium text-grey-dark'
      : 'text-sm leading-[18px] font-interMedium text-black';

    const discountedAmount = product.getDiscountedPrice(plan?.discount);
    const handleAddProduct = useCallback(() => {
      plan?.increaseLineItem(product, 1, 'pdp_recommendation');
    }, [plan]);

    const handleDeleteProduct = useCallback(() => {
      plan?.decreaseLineItem(product, 'pdp_recommendation');
    }, [plan]);
    const trackClick = () => {
      try {
        trackProductClicked(product, 'pdp_recommendation');
      } catch (error) {
        // ignore error
      }
    };

    return (
      <div className="flex w-full flex-col">
        <div
          className="flex w-full shrink-0 justify-start"
          onClick={trackClick}
        >
          {image &&
            (mode !== Mode.EDIT_SUBSCRIPTION ? (
              <Link
                href={{
                  pathname: `/products/${slug}`,
                  query: {
                    ...(mode && { mode }),
                  },
                }}
                className="w-full"
              >
                <div className="relative w-full rounded-xl border-solid border-black">
                  <Image
                    src={image}
                    alt="Cart Basket"
                    width={175}
                    height={150}
                    className="w-full rounded-xl hover:cursor-pointer"
                    onClick={closeCart}
                    sizes="175px"
                    loader={datocmsImageLoader}
                    quality={100}
                  />
                  {tertiaryDisplayTag && (
                    <TertiaryTag
                      tag={tertiaryDisplayTag}
                      fullWidth={!secondaryDisplayTag}
                      recommendation
                    />
                  )}
                  {secondaryDisplayTag && (
                    <SecondaryTag
                      tag={secondaryDisplayTag}
                      fullWidth={!tertiaryDisplayTag}
                      theme="recommendation"
                    />
                  )}
                </div>
              </Link>
            ) : (
              <div className="relative border-t border-grey-dark">
                <Image
                  src={image}
                  alt="Cart Basket"
                  width={175}
                  height={150}
                  className="rounded-xl"
                />
                {tertiaryDisplayTag && (
                  <TertiaryTag
                    tag={tertiaryDisplayTag}
                    fullWidth={!secondaryDisplayTag}
                  />
                )}
              </div>
            ))}
        </div>
        <div className="flex flex-col justify-between gap-2 py-4">
          {mode !== Mode.EDIT_SUBSCRIPTION ? (
            <Link
              href={{
                pathname: `/products/${slug}`,
                query: {
                  ...(mode && { mode }),
                },
              }}
              legacyBehavior
              onClick={trackClick}
            >
              <h5
                onClick={closeCart}
                className="line-clamp-2 min-h-[42px] font-interMedium text-sm leading-5 hover:cursor-pointer"
              >
                {name}
              </h5>
            </Link>
          ) : (
            <h5 className="line-clamp-2 min-h-[42px] font-interMedium text-xs leading-5">
              {name}
            </h5>
          )}
          {/* Price */}
          <div className="flex flex-row-reverse justify-end">
            <span
              className={`self-center text-sm lg:leading-[30px] ${discountStyle} pr-0 lg:pr-3`}
            >
              ${grossPrice?.toFixed(2)}
            </span>
            {discountable ? (
              <span className="pr-2 font-interSemibold text-sm leading-6 text-success-secondary lg:leading-[30px]">
                ${discountedAmount?.toFixed(2)}
              </span>
            ) : null}
          </div>

          {/* QTY */}
          {qty > 0 ? (
            <div className="flex h-[50px] w-full items-center justify-between">
              <div className="w-1/6" />
              <QuantityButton
                handleAddProduct={handleAddProduct}
                handleDeleteProduct={handleDeleteProduct}
                qty={qty}
                size="medium"
                page="pdp"
              />
            </div>
          ) : (
            <div className={'flex h-[50px] items-center justify-between'}>
              <ProductLikePill
                view="small"
                sku={product.sku}
                selected={false}
              />
              <div className="flex items-end">
                <Button
                  className="!min-w-[32px] !pr-0"
                  theme="quaternary"
                  onClick={handleAddProduct}
                >
                  <Image
                    src={'/assets/icons/cart-plus-icon.svg'}
                    alt="AddCartIcon"
                    width={32}
                    height={32}
                    className="rounded-l-xl"
                  />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default RecommendationProductTile;
